import React, { useState, useMemo, useEffect, useRef } from "react";
import * as am4core from "@amcharts/amcharts4/core";
import * as am4charts from "@amcharts/amcharts4/charts";
import am4themes_animated from "@amcharts/amcharts4/themes/animated";

am4core.useTheme(am4themes_animated);

/* =========================
   DUMMY DATA (TEST ONLY)
========================= */

const GROUPS = [
    {
        id: "financial",
        label: "Financial",
        total: 25,
        color: "#ff6b6b",
        worst: [
            { name: "Building A", value: "+42%" },
            { name: "Building D", value: "+38%" },
            { name: "Building F", value: "+31%" }
        ],
        best: [
            { name: "Building Q", value: "-22%" },
            { name: "Building T", value: "-19%" }
        ]
    },
    {
        id: "spatial",
        label: "Spatial",
        total: 15,
        color: "#4dd0e1",
        worst: [
            { name: "Building C", value: "+28%" },
            { name: "Building H", value: "+24%" }
        ],
        best: [
            { name: "Building N", value: "-18%" }
        ]
    },
    {
        id: "people",
        label: "People",
        total: 6,
        color: "#7aa2ff",
        worst: [
            { name: "Building B", value: "+19%" }
        ],
        best: [
            { name: "Building X", value: "-12%" }
        ]
    }
];





/* =========================
   STYLES
========================= */

const drawerSection = {
    display: "flex",
    flexDirection: "column",
    gap: "16px"
};

const donutWrap = {
    display: "flex",
    justifyContent: "center",
    padding: "10px 0 4px"
};

const groupHeader = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    cursor: "pointer",
    padding: "10px 4px",
    fontSize: "13px",
    fontWeight: 600,
    letterSpacing: "0.6px",
    textTransform: "uppercase",
    color: "#fff"
};

const groupMeta = {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    fontSize: "12px",
    color: "rgba(255,255,255,0.6)"
};

const listBlock = {
    paddingLeft: "6px",
    display: "flex",
    flexDirection: "column",
    gap: "6px",
    fontSize: "13px"
};

const listRow = (color) => ({
    display: "flex",
    justifyContent: "space-between",
    color: color
});

const divider = {
    height: "1px",
    background: "rgba(255,255,255,0.08)",
    margin: "6px 0"
};

/* =========================
   COMPONENT
========================= */

export default function OutlierSummaryDrawer() {

    /* Auto-expand the largest group */
    const defaultGroup = useMemo(() => {
        return GROUPS.reduce((max, g) => g.total > max.total ? g : max, GROUPS[0]).id;
    }, []);

    const [openGroup, setOpenGroup] = useState(defaultGroup);
	
	
	const donutRef = useRef(null);

	useEffect(() => {
	    if (!donutRef.current) {
	        return;
	    }

	    const chart = am4core.create(donutRef.current, am4charts.PieChart);
	    chart.innerRadius = am4core.percent(65);
	    chart.padding(0, 0, 0, 0);

	    chart.data = GROUPS.map(g => ({
	        id: g.id,
	        label: g.label,
	        value: g.total,
	        color: am4core.color(g.color)
	    }));

	    const pieSeries = chart.series.push(new am4charts.PieSeries());
	    pieSeries.dataFields.value = "value";
	    pieSeries.dataFields.category = "label";

	    pieSeries.slices.template.propertyFields.fill = "color";
	    pieSeries.slices.template.strokeOpacity = 0;
	    pieSeries.slices.template.cursorOverStyle = am4core.MouseCursorStyle.pointer;

	    /* External labels */
	    pieSeries.labels.template.text = "{category}";
	    pieSeries.labels.template.radius = 18;
	    pieSeries.labels.template.fill = am4core.color("#fff");
	    pieSeries.labels.template.fontSize = 11;
	    pieSeries.labels.template.fontWeight = 600;

	    pieSeries.ticks.template.strokeOpacity = 0.4;
	    pieSeries.ticks.template.stroke = am4core.color("#fff");
	    pieSeries.ticks.template.length = 8;

	    /* Highlight active group */
	    pieSeries.slices.template.adapter.add("opacity", (opacity, target) => {
	        const dataItem = target.dataItem?.dataContext;
	        if (!dataItem) {
	            return opacity;
	        }
	        return dataItem.id === openGroup ? 1 : 0.45;
	    });

	    /* Click slice → expand group */
	    pieSeries.slices.template.events.on("hit", ev => {
	        const id = ev.target.dataItem.dataContext.id;
	        setOpenGroup(prev => (prev === id ? null : id));
	    });
		
		
		// FORCE labels to render outside
		pieSeries.alignLabels = false;

		// Don’t let amCharts hide them
		pieSeries.labels.template.disabled = false;
		pieSeries.labels.template.hideOversized = false;
		pieSeries.labels.template.truncate = false;

		// Push labels outside the ring
		pieSeries.labels.template.radius = 5;
		pieSeries.labels.template.text = "{category}";
		pieSeries.labels.template.fill = am4core.color("#ffffff");
		pieSeries.labels.template.fontSize = 11;
		pieSeries.labels.template.fontWeight = 600;

		// Leader lines (ticks)
		pieSeries.ticks.template.disabled = false;
		pieSeries.ticks.template.strokeOpacity = 0.4;
		pieSeries.ticks.template.stroke = am4core.color("#ffffff");
		pieSeries.ticks.template.length = 10;

		// CRITICAL: allow labels outside container
		pieSeries.labels.template.wrap = true;
		chart.maskBullets = false;

	    chart.legend = undefined;

	    return () => {
	        chart.dispose();
	    };

	}, [openGroup]);

    const totalAll = GROUPS.reduce((sum, g) => sum + g.total, 0);

    return (
        <div style={drawerSection}>

            {/* =========================
               DOUGHNUT (TEST VERSION)
            ========================= */}

			<div style={donutWrap}>
			    <div
			        ref={donutRef}
			        style={{
			            width: "100%",
			            height: "160px",
						overflow:'visible'
			        }}
			    />
			</div>

            {/* =========================
               GROUP LIST
            ========================= */}

            {GROUPS.map(group => {
                const isOpen = openGroup === group.id;

                return (
                    <div key={group.id}>

                        <div
                            style={groupHeader}
                            onClick={() => setOpenGroup(isOpen ? null : group.id)}
                        >
                            <div>{group.label}</div>

                            <div style={groupMeta}>
                                <span>{group.total}</span>
                                <span>{isOpen ? "▴" : "▾"}</span>
                            </div>
                        </div>

                        {isOpen && (
                            <div>

                                <div style={divider} />

                                <div style={listBlock}>
                                    <div style={{ opacity: 0.6, fontSize: "12px" }}>
                                        Worst 10
                                    </div>

                                    {group.worst.map((row, i) => (
                                        <div key={i} style={listRow("#ff6b6b")}>
                                            <span>{row.name}</span>
                                            <span>{row.value}</span>
                                        </div>
                                    ))}
                                </div>

                                <div style={divider} />

                                <div style={listBlock}>
                                    <div style={{ opacity: 0.6, fontSize: "12px" }}>
                                        Best 10
                                    </div>

                                    {group.best.map((row, i) => (
                                        <div key={i} style={listRow("#6ee7b7")}>
                                            <span>{row.name}</span>
                                            <span>{row.value}</span>
                                        </div>
                                    ))}
                                </div>

                                <div style={divider} />
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
}